"use client";

import { useState, useRef, useEffect } from "react";
import type { VideoRotationDemoBlock } from "@mw/shared/payload-types";
import { getVideoUrl } from "@utilities/payload";
import { clsx } from "clsx";
import { Tabs, TabList, Tab, TabPanel } from "react-aria-components";
import { useTranslations } from "next-intl";
import { useInView } from "@ui/lib/useInView";
import { useFocusWithin } from "react-aria";
import * as m from "motion/react-m";
import { AnimatePresence } from "motion/react";

import { VideoPlayerContent } from "@ui/components/core/VideoPlayerContent";

// Re-export for backwards compatibility
export { VideoPlayerContent } from "@ui/components/core/VideoPlayerContent";
export type { VideoPlayerContentProps } from "@ui/components/core/VideoPlayerContent";

type VideoRotationDemoProps = {
  config?: Pick<NonNullable<VideoRotationDemoBlock>, "videos" | "enableOverlay">;
};

export function VideoRotationDemo({ config }: VideoRotationDemoProps) {
  const t = useTranslations("ClientKeys.video_rotation_demo");
  const videos = config?.videos || [];
  const enableOverlay = config?.enableOverlay ?? false;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [hasFocus, setHasFocus] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hideControlsTimeoutRef = useRef<number | null>(null);

  const inView = useInView(containerRef, { once: true, threshold: 0.1 });

  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: () => {
      setHasFocus(true);
      setShowControls(true);
      // Clear any pending hide timer
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    },
    onBlurWithin: () => {
      setHasFocus(false);
    },
  });

  // Auto-mute video when out of viewport
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && !isMuted) {
          setIsMuted(true);
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isMuted]);

  // Try to play video when it changes
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        // Autoplay might be blocked, which is fine - user can click play
      });
    }
  }, [currentIndex]);

  // Auto-hide controls on mobile after 3 seconds of inactivity (but keep visible when focused)
  useEffect(() => {
    const resetHideTimer = () => {
      setShowControls(true);

      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }

      hideControlsTimeoutRef.current = window.setTimeout(() => {
        // Don't hide if something is focused
        if (!hasFocus) {
          setShowControls(false);
        }
      }, 3000);
    };

    resetHideTimer();

    return () => {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, [isPlaying, hasFocus]);

  const handleInteraction = () => {
    if (!showControls) {
      setShowControls(true);

      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }

      hideControlsTimeoutRef.current = window.setTimeout(() => {
        // Don't hide if something is focused
        if (!hasFocus) {
          setShowControls(false);
        }
      }, 3000);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const selectVideo = (key: React.Key) => {
    if (key !== currentIndex) {
      setCurrentIndex(Number(key));
      // The useEffect will trigger play when videoUrl changes
    }
  };

  const handleVideoEnd = () => {
    if (videos.length === 1) {
      // If there's only one video, just restart it
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(() => {
          // Play might fail, but state will be accurate via events
        });
      }
    } else {
      // Auto-rotate to next video, wrap around to first video
      const nextIndex = (currentIndex + 1) % videos.length;
      setCurrentIndex(nextIndex);
      // The useEffect will trigger play when videoUrl changes
    }
  };

  if (!videos.length) {
    return null;
  }

  // Single video - no tabs needed
  if (videos.length === 1) {
    const video = videos[0];

    return (
      <div ref={containerRef} className='tw-relative tw-h-full tw-w-full tw-overflow-hidden' {...focusWithinProps}>
        <VideoPlayerContent
          video={video}
          videoRef={videoRef}
          isMuted={isMuted}
          showControls={showControls}
          inView={inView}
          enableOverlay={enableOverlay}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={handleVideoEnd}
          onInteraction={handleInteraction}
          toggleMute={toggleMute}
        />
      </div>
    );
  }

  // Multiple videos - use tabs
  return (
    <div ref={containerRef} className='tw-relative tw-h-full tw-w-full tw-overflow-hidden' {...focusWithinProps}>
      <Tabs selectedKey={currentIndex.toString()} onSelectionChange={selectVideo} className='tw-h-full'>
        {/* Video selector - horizontally scrolling with auto-centering */}
        <div
          className={clsx(
            "tw-absolute tw-bottom-4 md:tw-bottom-6 tw-z-10 tw-transition-opacity tw-duration-300",
            "tw-left-0 tw-right-0 tw-pointer-events-none",
            !showControls && "md:tw-opacity-100 tw-opacity-0",
          )}
        >
          <div
            className={clsx(
              "tw-flex tw-gap-3 tw-overflow-x-auto tw-overflow-y-hidden tw-scrollbar-none tw-p-1",
              "md:tw-pointer-events-auto",
              showControls && "tw-pointer-events-auto",
            )}
          >
            {/* Left spacer - grows to center content when space available, larger on desktop to clear play button */}
            <div className='tw-shrink-0 tw-flex-grow tw-min-w-4 md:tw-min-w-24 tw-basis-0 tw-pointer-events-none' />

            <TabList className='tw-flex tw-gap-3'>
              {videos.map((video, index) => {
                const thumbVideoUrl = video.media ? getVideoUrl(video.media) : null;
                if (!thumbVideoUrl) return null;

                return (
                  <Tab
                    key={index}
                    id={index.toString()}
                    className={clsx(
                      "tw-relative tw-shrink-0 tw-w-16 md:tw-w-24 tw-aspect-[3/2] tw-rounded-xl tw-transition-all tw-group tw-cursor-pointer",
                      "focus-visible:tw-outline-focus",
                    )}
                    aria-label={t("play_video", { title: video.title })}
                  >
                    {({ isSelected }) => (
                      <>
                        <div className='tw-absolute tw-inset-0 tw-rounded-xl tw-overflow-hidden'>
                          {inView && (
                            <video
                              src={thumbVideoUrl}
                              className='tw-w-full tw-h-full tw-object-cover'
                              muted
                              playsInline
                              preload='metadata'
                            />
                          )}
                          <div className='tw-absolute tw-inset-0 tw-bg-black/20 group-hover:tw-bg-black/0 tw-transition-colors' />
                        </div>
                        <AnimatePresence>
                          {isSelected && (
                            <m.div
                              className='tw-absolute tw-inset-0 tw-pointer-events-none tw-ring-2 tw-ring-white tw-ring-offset-2 tw-ring-offset-black/50 tw-rounded-xl group-focus-visible:tw-ring-blue-500'
                              initial={{ scale: 0.92, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              exit={{ scale: 0.95, opacity: 0 }}
                              transition={{ duration: 0.15, ease: "easeOut" }}
                            />
                          )}
                        </AnimatePresence>
                      </>
                    )}
                  </Tab>
                );
              })}
            </TabList>

            {/* Right spacer - grows to center content when space available */}
            <div className='tw-shrink-0 tw-flex-grow tw-min-w-4 tw-basis-0 tw-pointer-events-none' />
          </div>
        </div>
        {/* TabPanels for each video */}
        {videos.map((video, index) => (
          <TabPanel key={index} id={index.toString()} className='tw-absolute tw-inset-0 tw-h-full'>
            <VideoPlayerContent
              video={video}
              videoRef={index === currentIndex ? videoRef : undefined}
              isMuted={isMuted}
              showControls={showControls}
              inView={inView}
              enableOverlay={enableOverlay}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={handleVideoEnd}
              onInteraction={handleInteraction}
              toggleMute={toggleMute}
            />
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
}
